import React, { useState } from "react"

import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/solid"

import classNames from "src/classNames"

import { gql, useQuery } from "hooks/urql"

import AutocompleteSelect from "components/Forms/AutocompleteSelect"
import NewThread from "components/LegacyMessageThreads/Index/NewThread"
import LocalTime from "components/LocalTime"
import Pagination from "components/Pagination"
import UrqlLoadingIndicator from "components/WithLoadingIndicator/urql"

const MessageThreads = () => {
  const [userIds, setUserIds] = useState([])
  const [organization, setOrganization] = useState(undefined)
  const [school, setSchool] = useState(undefined)
  const [showHidden, setShowHidden] = useState(undefined)
  const [title, setTitle] = useState("")
  const [page, setPage] = useState(1)

  const [result] = useQuery({
    query: messageThreadsQuery,
    variables: {
      userIds: userIds.map(user => user.id),
      organizationId: organization?.value,
      schoolId: school?.value,
      showHidden,
      title,
      page,
    },
  })

  return (
    <>
      <NewThread userType="Admin" />
      <div className="flex flex-wrap gap-6">
        <div className="form-group min-w-[200px]">
          <label className="form-label w-100">
            Find by User
            <AutocompleteSelect
              isMulti
              api="/api/admins/users/autocomplete_full_name"
              onChange={selected => {
                setUserIds(selected)
              }}
              value={userIds}
            />
          </label>
        </div>
        <div className="form-group min-w-[200px]">
          <label className="form-label w-100">
            Find by Organization
            <AutocompleteSelect
              api="/api/admins/organizations/autocomplete_name"
              onChange={selected => {
                setOrganization(selected)
              }}
              value={organization}
            />
          </label>
        </div>
        <div className="form-group min-w-[200px]">
          <label className="form-label w-100">
            Find by School
            <AutocompleteSelect
              api="/api/admins/schools/autocomplete_name"
              onChange={selected => {
                setSchool(selected)
              }}
              value={school}
            />
          </label>
        </div>
        <div className="form-group min-w-[200px]">
          <label className="form-label w-full">
            <span>Find by Title</span>
            <input
              className="form-control w-full border border-solid !border-gray-300"
              onChange={e => setTitle(e.currentTarget.value)}
              value={title}
            />
          </label>
        </div>
        <div className="form-group flex items-end">
          <div className="form-check">
            <label className="form-check-label">
              Show Hidden Threads
              <input
                type="checkbox"
                checked={showHidden}
                className="form-check-input"
                onChange={() => {
                  setShowHidden(currentVal => !currentVal)
                }}
              />
              <span className="form-check-sign" />
            </label>
          </div>
        </div>
      </div>

      <UrqlLoadingIndicator result={result}>
        {({ data: { messageThreads } }) => (
          <>
            <div className="overflow-x-auto">
              <table className="table">
                <thead className="text-primary">
                  <tr>
                    <th>Title</th>
                    <th>Messages</th>
                    <th>Last Message</th>
                    <th>Participants</th>
                    <th>Created</th>
                  </tr>
                </thead>
                <tbody>
                  {messageThreads.data.map(thread => (
                    <tr
                      key={thread.id}
                      onClick={() => window.open(thread.showPath, "_blank")}
                      className={classNames(
                        "cursor-pointer hover:bg-white",
                        thread.hidden && "bg-brand-100"
                      )}
                    >
                      <td>
                        <span className="flex h-full items-center gap-2 align-middle text-lg font-medium">
                          {thread.hidden ? (
                            <EyeSlashIcon className="h-6 w-6 text-red-500" />
                          ) : (
                            <EyeIcon className="h-6 w-6 text-red-500" />
                          )}
                          {thread.title}
                        </span>
                      </td>
                      <td>{thread.messagesCount}</td>
                      <td>
                        {thread.lastMessage && (
                          <div className="flex flex-col">
                            <span className="italic">
                              <LocalTime
                                relative
                                timestamp={thread.lastMessage.createdAt}
                              />
                              , {thread.lastMessage.author.messagingDisplayName}{" "}
                              wrote:
                            </span>
                            <span className="max-w-[340px] truncate font-medium">
                              {thread.lastMessage.content}
                            </span>
                          </div>
                        )}
                      </td>
                      <td>
                        <ul className="list-none px-0">
                          {thread.users.map(user => (
                            <li key={user.id}>
                              <a
                                href={user.showPath}
                                onClick={e => e.stopPropagation()}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {user.messagingDisplayName}
                              </a>
                            </li>
                          ))}
                        </ul>
                      </td>
                      <td>
                        <div className="flex flex-col">
                          <LocalTime omitTime timestamp={thread.createdAt} />
                          <a
                            href={thread.author.showPath}
                            onClick={e => e.stopPropagation()}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {thread.author.messagingDisplayName}
                          </a>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination
              currentPage={messageThreads.currentPage}
              totalPages={messageThreads.totalPages}
              changePage={setPage}
            />
          </>
        )}
      </UrqlLoadingIndicator>
    </>
  )
}

const messageThreadsQuery = gql`
  query MessageThreads(
    $userIds: [ID!]
    $organizationId: ID
    $schoolId: ID
    $showHidden: Boolean
    $title: String
    $page: Int
  ) {
    messageThreads(
      userIds: $userIds
      organizationId: $organizationId
      schoolId: $schoolId
      showHidden: $showHidden
      title: $title
      page: $page
    ) {
      currentPage
      totalPages
      data {
        id
        hidden
        title
        showPath
        messagesCount: unfilteredMessagesCount
        createdAt
        author {
          id
          messagingDisplayName
          showPath
        }
        users {
          id
          type
          messagingDisplayName
          showPath
        }
        lastMessage: unfilteredLastMessage {
          id
          content
          createdAt
          author {
            id
            type
            messagingDisplayName
            showPath
          }
        }
      }
    }
  }
`

export default MessageThreads
